import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const now = new Date();

const TYPECLS_CLASS = {
  정규: 'typecls-regular',
  특별: 'typecls-special',
  보충: 'typecls-supplement',
  방과후: 'typecls-afterschool',
  행사: 'typecls-event',
  휴업: 'typecls-closure',
  기타: 'typecls-etc',
};

function typeclsClassName(typecls) {
  return TYPECLS_CLASS[typecls] || 'typecls-etc';
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toDateStr(year, month, day) {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

const TODAY_STR = toDateStr(now.getFullYear(), now.getMonth() + 1, now.getDate());

function monthRange(year, month) {
  const lastDay = new Date(year, month, 0).getDate();
  return { start: toDateStr(year, month, 1), end: toDateStr(year, month, lastDay) };
}

function buildWeeks(year, month) {
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

function formatDate(iso) {
  if (!iso) return '';
  return iso.replace('T', ' ').slice(0, 16);
}

export default function MobileHome({ onNavigate }) {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [grade, setGrade] = useState(1);
  const [byDate, setByDate] = useState({});
  const [academicByDate, setAcademicByDate] = useState({});
  const [selectedDate, setSelectedDate] = useState(TODAY_STR);
  const [posts, setPosts] = useState([]);

  const load = useCallback(() => {
    const { start, end } = monthRange(year, month);
    api.get('/teacher/schedule', { params: { start, end } }).then((res) => {
      const grouped = {};
      res.data.forEach((e) => {
        grouped[e.schedate] = grouped[e.schedate] || [];
        grouped[e.schedate].push(e);
      });
      setByDate(grouped);
    });
    if (grade === 0) {
      setAcademicByDate({});
    } else {
      api.get('/admin/academic-schedule', { params: { start, end, grade } }).then((res) => {
        const grouped = {};
        res.data.forEach((e) => {
          grouped[e.schedate] = grouped[e.schedate] || [];
          grouped[e.schedate].push(e);
        });
        setAcademicByDate(grouped);
      });
    }
  }, [year, month, grade]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get('/board/posts').then((res) => setPosts(res.data.slice(0, 6)));
  }, []);

  const goPrevMonth = () => {
    if (month === 1) {
      setYear((y) => y - 1);
      setMonth(12);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (month === 12) {
      setYear((y) => y + 1);
      setMonth(1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const weeks = buildWeeks(year, month);
  const selectedEntries = byDate[selectedDate] || [];
  const selectedAcademicEntries = academicByDate[selectedDate] || [];

  return (
    <div className="page mobile-page">
      <div className="card">
        <div className="board-header">
          <div className="mobile-home-month-nav">
            <button className="btn-ghost" type="button" onClick={goPrevMonth}>◀</button>
            <h3>내 일정 ({month}월)</h3>
            <button className="btn-ghost" type="button" onClick={goNextMonth}>▶</button>
          </div>
          <select
            className="field-input mobile-home-grade-select"
            value={grade}
            onChange={(e) => setGrade(Number(e.target.value))}
          >
            <option value={1}>1학년 학사일정</option>
            <option value={2}>2학년 학사일정</option>
            <option value={3}>3학년 학사일정</option>
            <option value={0}>학사일정 제외</option>
          </select>
        </div>

        <table className="academic-calendar-table mobile-home-calendar">
          <thead>
            <tr>
              {WEEKDAYS.map((w) => (
                <th key={w}>{w}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, wi) => (
              <tr key={wi}>
                {week.map((d, di) => {
                  if (d === null) return <td key={di} className="academic-calendar-empty" />;
                  const dateStr = toDateStr(year, month, d);
                  const entries = byDate[dateStr] || [];
                  const academicEntries = academicByDate[dateStr] || [];
                  const isSelected = dateStr === selectedDate;
                  return (
                    <td
                      key={di}
                      className={`academic-calendar-day mobile-home-day${isSelected ? ' selected' : ''}`}
                      onClick={() => setSelectedDate(dateStr)}
                    >
                      <div className="academic-calendar-daynum">{d}</div>
                      {academicEntries.slice(0, 1).map((e) => (
                        <div key={`a-${e.id}`} className={`academic-calendar-entry ${typeclsClassName(e.typecls)}`}>
                          {e.typecls}
                        </div>
                      ))}
                      {entries.slice(0, 1).map((e) => (
                        <div key={`m-${e.id}`} className="teacher-schedule-entry">{e.title}</div>
                      ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        <h4 className="teacher-schedule-section-label">{selectedDate}{selectedDate === TODAY_STR ? ' (오늘)' : ''}</h4>
        {selectedAcademicEntries.map((e) => (
          <div key={`a-${e.id}`} className="academic-calendar-entry-row">
            <span>
              <span className={`academic-calendar-typecls-badge ${typeclsClassName(e.typecls)}`}>{e.typecls}</span>
              {e.title && <span> {e.title}</span>}
            </span>
          </div>
        ))}
        {selectedEntries.length === 0 && selectedAcademicEntries.length === 0 && (
          <div className="empty-row">일정이 없습니다.</div>
        )}
        {selectedEntries.map((e) => (
          <div key={e.id} className="academic-calendar-entry-row">
            <span>{e.title}</span>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="board-header">
          <h3>게시판</h3>
          <button className="btn-link" type="button" onClick={() => onNavigate('board')}>
            전체보기
          </button>
        </div>
        {posts.length === 0 && <div className="empty-row">등록된 게시글이 없습니다.</div>}
        {posts.map((p) => (
          <div key={p.postid} className="academic-calendar-entry-row" onClick={() => onNavigate('board')}>
            <span>{p.title}</span>
            <span>{p.author_name} · {formatDate(p.createat)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
